import React, { useEffect, useRef, useState } from "react";
import { ClipboardList, AlertTriangle, CheckCircle2, Send, Pencil, Trash2, Plus, X, ChevronDown, ChevronUp } from "lucide-react";
import { PageHeader, Footer } from "@/components/common";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SAFETY_CATEGORIES, STATUSES, MONTHS, YEARS, formatDateID, todayISO } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const FIVES_CATEGORIES = ["Housekeeping", "Pencemaran lingkungan", "Kesehatan"];

function statusTextColor(s) {
  switch (s) {
    case "Complete": return "#16a34a";
    case "In Progress": return "#ca8a04";
    case "Waiting Part": return "#2563eb";
    case "No Progress": return "#dc2626";
    default: return "#64748b";
  }
}

// Client-side compression: resize to max 1024px wide and re-encode as JPEG ~40%.
// No raw fallback: if encoding fails we throw so the caller rejects the upload — this guarantees
// only compressed JPEG data (never a large PNG) is ever persisted to the database.
async function compressImage(file, maxW = 1024, quality = 0.4) {
  const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
  const img = await new Promise((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = dataUrl; });
  const scale = Math.min(1, maxW / img.width);
  const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, w, h); // flatten transparency so JPEG has white (not black) bg
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

function MediaUpload({ label, value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const url = await compressImage(f);
      onChange(url);
    } catch (err) {
      toast.error("Gagal memproses gambar. Silakan unggah ulang.");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div>
      <p className="text-xs font-medium text-slate-600 mb-1">{label}</p>
      <div onClick={() => ref.current?.click()} className="border-2 border-dashed border-slate-300 rounded-lg h-32 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-slate-50 overflow-hidden relative" data-testid={testid}>
        {busy ? <span className="text-xs text-slate-400">Mengompres...</span> : value ? (
          <>
            <img src={value} alt="preview" className="w-full h-full object-contain bg-slate-100" />
            <button type="button" onClick={(e) => { e.stopPropagation(); onChange(null); }} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 text-red-500 shadow"><Trash2 className="w-3.5 h-3.5" /></button>
          </>
        ) : (
          <><Plus className="w-6 h-6 text-slate-400" /><span className="text-xs text-slate-400 mt-1">Add Media</span></>
        )}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
      </div>
    </div>
  );
}

const emptyForm = {
  finding_type: "safety",
  date: todayISO(),
  description: "",
  category: SAFETY_CATEGORIES[0],
  area_equipment: "",
  hazard_potential: "",
  finding_image: null,
  finding_image2: null,
  action_date: todayISO(),
  action_plan: "",
  action_taken: "",
  action_image: null,
  action_image2: null,
  status: "No Progress",
};

function ReportImg({ src, alt }) {
  return <img src={src} alt={alt} className="w-full h-32 object-cover bg-slate-100" />;
}

function ReportCard({ r, canEdit, onEdit, onDelete }) {
  const is5s = r.finding_type === "5s";
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/finding-reports/${r.id}`).then((res) => { if (active) setFull(res.data); }).catch(() => {}).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [r.id]);
  const d = full || r;
  const complete = d.status === "Complete";
  const statusCol = complete ? "#15803d" : "#d97706";
  const area = (is5s ? d.area : d.equipment_area) || "-";
  const fImgs = [d.finding_image, d.finding_image2].filter(Boolean);
  const aImgs = [d.action_image, d.action_image2].filter(Boolean);
  const Skeleton = () => <div className="w-full h-32 bg-slate-100 animate-pulse flex items-center justify-center text-xs text-slate-300">Memuat...</div>;
  const NoImg = () => <div className="w-full h-32 bg-slate-100 flex items-center justify-center text-xs text-slate-400 font-medium">No Image</div>;
  return (
    <div className={`rounded-2xl border-2 overflow-hidden shadow-sm ${complete ? "border-green-600" : "border-red-800"}`} data-testid={`report-${r.id}`}>
      <div className="bg-red-800 text-white px-3 py-2 flex items-center justify-between">
        <span className="font-bold text-[11px] tracking-wide">EVIDENCE REPORT FINDING {is5s ? "5S" : "SAFETY"}</span>
        {canEdit && (
          <div className="flex gap-1">
            <button onClick={() => onEdit(r)} className="p-1.5 rounded bg-white/15 hover:bg-white/25" data-testid={`report-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => onDelete(r)} className="p-1.5 rounded bg-white/15 hover:bg-white/25" data-testid={`report-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 text-[11px] border-b">
        <span className="font-medium truncate">Area / Equipment : {area}</span>
        <span className="text-slate-600 whitespace-nowrap ml-2">Tanggal: {formatDateID(d.date)}</span>
      </div>
      <div className={`p-2 ${complete ? "bg-green-50" : "bg-red-50"}`}>
        <div className="grid grid-cols-2 gap-2">
          {/* LEFT: SAFETY FINDING */}
          <div className="space-y-2">
            <div className="rounded-lg border border-red-300 bg-white overflow-hidden">
              <div className="bg-red-800 text-white px-2 py-1 flex items-center gap-1 text-[11px] font-bold"><AlertTriangle className="w-3.5 h-3.5" /> SAFETY FINDING</div>
              <div className="space-y-1">
                {loading ? <Skeleton /> : fImgs.length ? fImgs.map((s, i) => <ReportImg key={i} src={s} alt={`finding-${i}`} />) : <NoImg />}
              </div>
            </div>
            <div className="text-xs space-y-1 px-0.5">
              <p><span className="font-bold text-slate-800">Description Finding:</span> {d.description || "-"}</p>
              <p className="flex items-start gap-1.5 font-bold text-slate-800"><AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" /> <span>Hazard Potential: <span className="font-normal">{d.hazard_potential || "-"}</span></span></p>
            </div>
          </div>
          {/* RIGHT: ACTION PLAN */}
          <div className="space-y-2">
            <div className="rounded-lg border border-green-300 bg-white overflow-hidden">
              <div className="bg-green-700 text-white px-2 py-1 flex items-center gap-1 text-[11px] font-bold"><CheckCircle2 className="w-3.5 h-3.5" /> ACTION PLAN</div>
              <div className="space-y-1">
                {loading ? <Skeleton /> : aImgs.length ? aImgs.map((s, i) => <ReportImg key={i} src={s} alt={`action-${i}`} />) : <NoImg />}
              </div>
            </div>
            <div className="text-xs space-y-1 px-0.5">
              <p><span className="font-bold text-slate-800">Description Action Plan:</span> {d.action_plan || "-"}</p>
              <p className="flex items-start gap-1.5 text-slate-800"><CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" /> <span><span className="font-bold">Tindakan Terambil:</span> {d.action_taken || "-"}</span></p>
              <p className="flex items-center gap-1.5 text-slate-800"><CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: statusCol }} /> <span className="font-bold">STATUS: <span style={{ color: statusCol }} data-testid={`report-status-${r.id}`}>{d.status?.toUpperCase()}</span></span></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InputFinding() {
  const { canEdit } = useAuth();
  const _now = new Date();
  const [month, setMonth] = useState(_now.getMonth() + 1);
  const [year, setYear] = useState(_now.getFullYear());
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [reports, setReports] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    const res = await api.get("/finding-reports", { params: { month, year, status: statusFilter } });
    setReports(res.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, year, statusFilter]);

  const categoryOptions = form.finding_type === "5s" ? FIVES_CATEGORIES : SAFETY_CATEGORIES;
  const safetyReports = reports.filter((r) => r.finding_type === "safety");
  const fiveSReports = reports.filter((r) => r.finding_type === "5s");

  const resetForm = () => { setForm(emptyForm); setEditingId(null); };
  const closeForm = () => { setShowForm(false); resetForm(); };
  const openCreate = () => { resetForm(); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const buildPayload = () => {
    const base = {
      finding_type: form.finding_type, date: form.date, description: form.description,
      hazard_potential: form.hazard_potential, finding_image: form.finding_image, finding_image2: form.finding_image2,
      action_image: form.action_image, action_image2: form.action_image2, action_plan: form.action_plan,
      action_taken: form.action_taken, action_date: form.action_date, status: form.status,
    };
    if (form.finding_type === "5s") return { ...base, area: form.area_equipment, category: form.category };
    return { ...base, category: form.category, equipment_area: form.area_equipment };
  };

  const submit = async () => {
    if (!form.description) { toast.error("Description wajib diisi"); return; }
    try {
      if (editingId) {
        const ep = form.finding_type === "5s" ? `/findings/5s/${editingId}` : `/findings/safety/${editingId}`;
        const p = buildPayload();
        delete p.finding_type;
        await api.put(ep, p);
        toast.success("Report diperbarui");
      } else {
        await api.post("/finding-reports", buildPayload());
        toast.success("Report berhasil disubmit");
      }
      const _d = new Date(form.date);
      const _fm = _d.getMonth() + 1, _fy = _d.getFullYear();
      closeForm();
      setStatusFilter("all");
      if (_fm !== month || _fy !== year) { setMonth(_fm); setYear(_fy); } else { load(); }
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Anda tidak punya akses edit" : "Gagal menyimpan report");
    }
  };

  const startEdit = async (r) => {
    setShowForm(true);
    setEditingId(r.id);
    let full = r;
    try { const res = await api.get(`/finding-reports/${r.id}`); full = res.data; } catch (e) {}
    setForm({
      finding_type: full.finding_type,
      date: full.date?.slice(0, 10) || todayISO(),
      description: full.description || "",
      category: full.category || (full.finding_type === "5s" ? FIVES_CATEGORIES[0] : SAFETY_CATEGORIES[0]),
      area_equipment: full.finding_type === "5s" ? (full.area || "") : (full.equipment_area || ""),
      hazard_potential: full.hazard_potential || "",
      finding_image: full.finding_image || null,
      finding_image2: full.finding_image2 || null,
      action_date: full.action_date?.slice(0, 10) || todayISO(),
      action_plan: full.action_plan || "",
      action_taken: full.action_taken || "",
      action_image: full.action_image || null,
      action_image2: full.action_image2 || null,
      status: full.status || "No Progress",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const doDelete = async () => {
    const r = deleteTarget; setDeleteTarget(null);
    try {
      const ep = r.finding_type === "5s" ? `/findings/5s/${r.id}` : `/findings/safety/${r.id}`;
      await api.delete(ep);
      toast.success("Report dihapus");
      load();
    } catch (e) { toast.error("Gagal menghapus"); }
  };

  const ReportSection = ({ title, color, list, testid }) => (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1.5 h-6 rounded" style={{ background: color }} />
        <h2 className="text-xl font-extrabold text-slate-800">{title}</h2>
        <span className="text-xs bg-slate-100 text-slate-500 rounded-full px-2 py-0.5 font-medium">{list.length}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid={testid}>
        {list.map((r) => <ReportCard key={r.id} r={r} canEdit={canEdit} onEdit={startEdit} onDelete={setDeleteTarget} />)}
        {list.length === 0 && <p className="text-slate-400 col-span-full text-center py-8">Belum ada data untuk periode ini</p>}
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6">
      <PageHeader icon={ClipboardList} title="INPUT FINDING" subtitle="Input temuan dan rencana tindakan (Safety / 5S)"
        right={<div className="flex items-center gap-2 flex-wrap">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}><SelectTrigger className="h-9 w-28" data-testid="if-filter-month"><SelectValue /></SelectTrigger><SelectContent>{MONTHS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}</SelectContent></Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}><SelectTrigger className="h-9 w-24"><SelectValue /></SelectTrigger><SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="h-9 w-36" data-testid="if-filter-status"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">Semua Status</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          <Button className="bg-[#0b1220] hover:bg-slate-800 h-9" onClick={showForm ? closeForm : openCreate} data-testid="if-toggle-form">
            {showForm ? <><ChevronUp className="w-4 h-4 mr-1" />Tutup Form</> : <><Plus className="w-4 h-4 mr-1" />Tambah Finding</>}
          </Button>
        </div>} />

      {/* FORM (hidden until Tambah Finding clicked) */}
      {showForm && (
        <div className="mb-8 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-center gap-2 mb-3"><ClipboardList className="w-5 h-5 text-blue-600" /><h2 className="font-bold text-slate-800">{editingId ? "Edit Finding" : "Input Finding Baru"}</h2></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-4">
            <div className="bg-white rounded-xl border-2 border-red-700 overflow-hidden">
              <div className="bg-red-700 text-white px-4 py-3 flex items-center gap-2 font-bold"><AlertTriangle className="w-5 h-5" /> Detail Temuan (Finding Details)</div>
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="if-date" /></div>
                  <div><Label className="text-xs">Category Finding</Label>
                    <Select value={form.finding_type} onValueChange={(v) => setForm((f) => ({ ...f, finding_type: v, category: v === "5s" ? FIVES_CATEGORIES[0] : SAFETY_CATEGORIES[0] }))}>
                      <SelectTrigger data-testid="if-ftype"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="safety">Finding Safety</SelectItem><SelectItem value="5s">Finding 5S</SelectItem></SelectContent></Select></div>
                </div>
                <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="if-desc" /></div>
                <div><Label className="text-xs">Category</Label>
                  <Select value={form.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="if-cat"><SelectValue /></SelectTrigger>
                    <SelectContent>{categoryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Area / Equipment</Label><Input value={form.area_equipment} onChange={(e) => set("area_equipment", e.target.value)} data-testid="if-area" /></div>
                <div><Label className="text-xs">Potensial Hazard</Label><Input value={form.hazard_potential} onChange={(e) => set("hazard_potential", e.target.value)} data-testid="if-hazard" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <MediaUpload label="Gambar 1: Kondisi Awal 1" value={form.finding_image} onChange={(v) => set("finding_image", v)} testid="if-img1" />
                  <MediaUpload label="Gambar 2: Kondisi Awal 2" value={form.finding_image2} onChange={(v) => set("finding_image2", v)} testid="if-img2" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border-2 border-green-700 overflow-hidden">
              <div className="bg-green-700 text-white px-4 py-3 flex items-center gap-2 font-bold"><CheckCircle2 className="w-5 h-5" /> Rencana &amp; Eksekusi Tindakan (Action Plan &amp; Execution)</div>
              <div className="p-4 space-y-3">
                <div><Label className="text-xs">Date</Label><Input type="date" value={form.action_date} onChange={(e) => set("action_date", e.target.value)} data-testid="if-adate" /></div>
                <div><Label className="text-xs">Description Plan</Label><Textarea value={form.action_plan} onChange={(e) => set("action_plan", e.target.value)} data-testid="if-plan" /></div>
                <div><Label className="text-xs">Execution Detail</Label><Textarea value={form.action_taken} onChange={(e) => set("action_taken", e.target.value)} data-testid="if-exec" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <MediaUpload label="Gambar 3: Kondisi Setelahnya 1" value={form.action_image} onChange={(v) => set("action_image", v)} testid="if-img3" />
                  <MediaUpload label="Gambar 4: Kondisi Setelahnya 2" value={form.action_image2} onChange={(v) => set("action_image2", v)} testid="if-img4" />
                </div>
                <div><Label className="text-xs">Status</Label>
                  <Select value={form.status} onValueChange={(v) => set("status", v)}><SelectTrigger data-testid="if-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={closeForm} data-testid="if-cancel"><X className="w-4 h-4 mr-1" /> Batal</Button>
            <Button className="bg-[#0b1220] hover:bg-slate-800" onClick={submit} data-testid="if-submit"><Send className="w-4 h-4 mr-1" /> {editingId ? "Update Report" : "Submit Report"}</Button>
          </div>
        </div>
      )}

      {/* REPORTS split by category */}
      <ReportSection title="Report Finding Safety" color="#b91c1c" list={safetyReports} testid="if-reports-safety" />
      <ReportSection title="Report Finding 5S" color="#2563eb" list={fiveSReports} testid="if-reports-5s" />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus Finding Report?</AlertDialogTitle>
            <AlertDialogDescription>Report ini juga akan terhapus dari menu Finding {deleteTarget?.finding_type === "5s" ? "5S" : "Safety"}. Aksi tidak dapat dibatalkan.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={doDelete} data-testid="report-del-confirm">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
}
